/** NEX line shape for display qty. */
export type NexLineQtyInput = {
    qtyRec?: number;
    qtyOnOrder?: number;
    qtyReturned?: number;
    qty?: number;
    quantity?: number;
};

export type NexLineQuantityBasis = 'qtyRec' | 'qtyOnOrder';

export function normalizeNexLineQuantityBasis(raw: unknown): NexLineQuantityBasis {
    return raw === 'qtyOnOrder' ? 'qtyOnOrder' : 'qtyRec';
}

function nonZeroQty(value: number | null | undefined): number | undefined {
    if (value == null || !Number.isFinite(value) || Math.abs(value) <= 1e-9) return undefined;
    return value;
}

/**
 * Quantity from a NEX detail line for UI display.
 * Received and sublet invoices use only the org basis: `qtyOnOrder` or `qtyRec`.
 * The other field is never read. Return invoices use quantity returned.
 *
 * Older ordered-quantity matches copied quantity received into `qtyOnOrder` and kept
 * the ordered quantity on `qty`. When `qtyOnOrder` is 0 and `qtyRec` is 0, `qty` is
 * that ordered quantity.
 */
export function getNexLineDisplayQty(
    nex: NexLineQtyInput | null | undefined,
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    basis: NexLineQuantityBasis = 'qtyRec',
): number {
    if (nex == null) return 0;
    const isReturnInvoice = invoiceType === 'returned' || invoiceType === 'return_slip';
    if (isReturnInvoice) {
        const ret = nex.qtyReturned;
        if (ret != null && Number.isFinite(ret) && Math.abs(ret) > 1e-9) return ret;
        const rec = nex.qtyRec;
        if (rec != null && Number.isFinite(rec) && Math.abs(rec) > 1e-9) return rec;
        return 0;
    }
    if (basis === 'qtyOnOrder') {
        const onOrder = nex.qtyOnOrder;
        if (onOrder != null && Number.isFinite(onOrder) && Math.abs(onOrder) > 1e-9) return onOrder;
        const received = nex.qtyRec;
        const receivedIsZero = received == null || (Number.isFinite(received) && Math.abs(received) <= 1e-9);
        const keptAtMatch = nonZeroQty(nex.qty) ?? nonZeroQty(nex.quantity);
        if (receivedIsZero && keptAtMatch != null) return keptAtMatch;
        return onOrder != null && Number.isFinite(onOrder) ? onOrder : 0;
    }
    const rec = nex.qtyRec;
    return rec != null && Number.isFinite(rec) ? rec : 0;
}

export function getSignedNexLineDisplayQty(
    nex: NexLineQtyInput | null | undefined,
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    basis: NexLineQuantityBasis = 'qtyRec',
): number {
    const qty = getNexLineDisplayQty(nex, invoiceType, basis);
    return invoiceType === 'returned' || invoiceType === 'return_slip' ? -Math.abs(qty) : qty;
}

export function getSignedNexLineDisplayTotal(
    total: number | null | undefined,
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet'
): number {
    const v = total ?? 0;
    return invoiceType === 'returned' || invoiceType === 'return_slip' ? -Math.abs(v) : v;
}

/** Hover copy for quantity-received columns (invoice document vs Nexsyis PO line). */
export const QTY_REC_UI_TOOLTIP =
    'Reflects quantity received on the invoice (from the document) and quantity received in Nexsyis on the matched or suggested purchase order line.';
export const QTY_ON_ORDER_UI_TOOLTIP =
    'Reflects quantity on the invoice (from the document) and quantity on order in Nexsyis on the matched or suggested purchase order line.';
export const QTY_RET_UI_TOOLTIP =
    'Reflects quantity returned on the credit/return invoice (from the document) and quantity returned in Nexsyis on the matched or suggested return line.';

/**
 * Header text for qty-received columns. Non-breaking space keeps "QTY REC." on one line in narrow cells (uppercase styling is applied where needed).
 */
export const QTY_REC_COLUMN_LABEL = 'Qty\u00A0rec.';
export const QTY_ON_ORDER_COLUMN_LABEL = 'Qty\u00A0ord.';
export const QTY_RET_COLUMN_LABEL = 'Qty\u00A0ret.';

export function getQtyColumnLabel(
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    basis: NexLineQuantityBasis = 'qtyRec',
): string {
    if (invoiceType === 'returned' || invoiceType === 'return_slip') return QTY_RET_COLUMN_LABEL;
    return basis === 'qtyOnOrder' ? QTY_ON_ORDER_COLUMN_LABEL : QTY_REC_COLUMN_LABEL;
}

export function getQtyColumnTooltip(
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    basis: NexLineQuantityBasis = 'qtyRec',
): string {
    if (invoiceType === 'returned' || invoiceType === 'return_slip') return QTY_RET_UI_TOOLTIP;
    return basis === 'qtyOnOrder' ? QTY_ON_ORDER_UI_TOOLTIP : QTY_REC_UI_TOOLTIP;
}

export function getQtyNexDescriptor(
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    basis: NexLineQuantityBasis = 'qtyRec',
): string {
    if (invoiceType === 'returned' || invoiceType === 'return_slip') return 'ret.';
    return basis === 'qtyOnOrder' ? 'ord.' : 'rec.';
}
